import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, MenuItem, Paper,
  Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import api from "../../config/axios";
import { PLUS_PROTOCOLS } from "../../config/plusProtocols";

const MINUTE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function EditPrescriptions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [prescriptions, setPrescriptions] = useState({});
  const [durations, setDurations] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Warn on browser close / refresh
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Intercept the browser Back button while there are unsaved changes.
  // A dummy history entry is pushed so the first Back press lands here; we
  // re-push it to stay on the page and show the same unsaved-changes dialog.
  useEffect(() => {
    if (!isDirty) return;
    window.history.pushState(null, "", window.location.href);
    const handlePop = () => {
      window.history.pushState(null, "", window.location.href);
      setConfirmOpen(true);
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [isDirty]);

  useEffect(() => {
    api.get(`/patients/${id}/prescriptions-plus`)
      .then(({ data }) => {
        setPrescriptions(data);
        const initial = {};
        Object.entries(data).forEach(([protocolId, days]) => {
          const first = Object.values(days)[0];
          if (first) initial[protocolId] = first.sessionDuration / 60;
        });
        setDurations(initial);
      })
      .catch(() => {
        toast.error("Error al cargar las prescripciones.");
        navigate("/patients");
      })
      .finally(() => setFetching(false));
  }, [id, navigate]);

  const handleChange = (protocolId, minutes) => {
    setDurations(prev => ({ ...prev, [protocolId]: minutes }));
    setIsDirty(true);
  };

  const handleBack = () => {
    if (isDirty) setConfirmOpen(true);
    else navigate("/patients");
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updates = [];
      Object.entries(prescriptions).forEach(([protocolId, days]) => {
        const minutes = durations[protocolId];
        if (minutes == null) return;
        Object.values(days).forEach(({ id: prescriptionId }) => {
          updates.push({ id: prescriptionId, sessionDuration: minutes * 60 });
        });
      });
      await api.put(`/patients/${id}/prescriptions-plus`, updates);
      setIsDirty(false);
      toast.success("Prescripciones actualizadas correctamente.");
      navigate("/patients");
    } catch {
      toast.error("Error al guardar las prescripciones.");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ minWidth: 0 }}
        >
          Volver
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Duración de actividades
        </Typography>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
        RehadayApp
      </Typography>

      <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: "hidden", mb: 3, display: "inline-block" }}>
        <TableContainer>
          <Table size="small" sx={{ width: "auto" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white", fontWeight: 700 }}>
                  Actividad
                </TableCell>
                <TableCell align="center" sx={{ color: "white", fontWeight: 700, width: 140 }}>
                  Duración
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PLUS_PROTOCOLS.map((protocol, i) => (
                <TableRow
                  key={protocol.id}
                  sx={{ backgroundColor: i % 2 === 0 ? "white" : "grey.50" }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <img
                        src={protocol.icon}
                        alt={protocol.name}
                        style={{ width: 36, height: 36, objectFit: "contain" }}
                      />
                      <Typography variant="body2">{protocol.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Select
                      size="small"
                      value={durations[protocol.id] ?? 5}
                      onChange={e => handleChange(protocol.id, e.target.value)}
                      sx={{ minWidth: 90 }}
                    >
                      {MINUTE_OPTIONS.map(m => (
                        <MenuItem key={m} value={m}>{m} min</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Sticky save bar */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          py: 2,
          backgroundColor: "background.default",
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          color="success"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading}
        >
          Guardar cambios
        </Button>
      </Box>

      {/* Unsaved changes dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cambios sin guardar</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tienes cambios sin guardar. Si sales ahora, se perderán.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Seguir editando</Button>
          <Button color="error" onClick={() => navigate("/patients")}>Salir sin guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
